/**
 * Background refresh for the tours list while the user is in the dashboard.
 *
 * - Polls every POLL_INTERVAL_MS while the tab is visible.
 * - Pauses when the tab is hidden so we don't burn quota in background tabs.
 * - Refreshes immediately on tab-visibility return (covers the "I switched
 *   tabs and came back" case where the user expects fresh data).
 *
 * The skeleton/empty-state gate in Dashboard ignores `loading` when there's
 * already data, so background polls don't flash skeletons.
 */

import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from 'app/hooks';
import { fetchTours } from 'app/services/redux/slices';

const POLL_INTERVAL_MS = 30_000;

export function useToursPolling(): void {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.authState.user);

  useEffect(() => {
    if (!user) return undefined;
    if (typeof document === 'undefined') return undefined;

    let intervalId: ReturnType<typeof setInterval> | null = null;

    const refresh = () => {
      dispatch(fetchTours(user));
    };

    const startPolling = () => {
      if (intervalId !== null) return;
      intervalId = setInterval(refresh, POLL_INTERVAL_MS);
    };

    const stopPolling = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refresh(); // immediate catch-up on return
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [dispatch, user]);
}
